import React from 'react';
import { connect } from 'react-redux';
import * as actions from 'store/state/app/actions';
import { useLocale } from 'locale';

import { UpdateFrequency } from '../config';
import { ToggleField, LabelTextMode } from 'ds/components/input/ToggleField';
import { SaveSearchButtonWrapper } from './styled';
import { State } from 'store/state';
import { addSaveSearchData, findSavedSearch, hasSavedSearch, previousSavedSearchIdSelector } from 'store/state/selectors/savedSearchPage';
import { isSearchListLoadingSelector } from 'store/state/domainData/selectors';
import SaveSearchMobileButton from './SaveSearchMobileButton';
import PostSavedModal from '../post-saved-modal/PostSavedModal';
import { NotificationBannerMessage } from 'components/notification-banner/types';
import ExistingSearchModal from '../existing-search-modal/ExistingSearchModal';
import EnhanceSearchModal from '../enhance-search-modal/EnhanceSearchModal';
import { useSendEvent } from 'analytics/useSendEvent';
import { BannerType } from 'components/notification-banner/bannersConfig';
import { savedSearchActiveModalSelector } from 'store/state/app/selectors';
import { SavedSearchActiveModal } from '../shared/active-modal';

interface SaveSearchButtonProps {
  onChange: (saveSilently?: boolean) => void;
  disabled: boolean;
  isToggled: boolean;
  label?: string;
  isButton?: boolean;
  labelTextMode?: LabelTextMode;
  noMargin?: boolean;
  setNotificationBannerMessage: (message: NotificationBannerMessage) => void;
  activeModal: SavedSearchActiveModal | null;
  setActiveModal: (modal: SavedSearchActiveModal | null) => void;
}

const SaveSearchButton: React.FC<SaveSearchButtonProps> = (props) => {
  const {
    onChange,
    disabled,
    isButton,
    isToggled,
    label,
    labelTextMode,
    noMargin,
    setNotificationBannerMessage,
    activeModal,
    setActiveModal,
  } = props;
  const { t } = useLocale();
  const sendEvent = useSendEvent();

  const handleClosePostSavedModal = () => {
    setActiveModal(null);
    setNotificationBannerMessage({
      typeId: BannerType.SearchSaved,
      timeout: 5000,
    });
  };

  const handleCreatedByExistingSearchModal = () => {
    setActiveModal(SavedSearchActiveModal.PostSaved);
  };

  const handleUpdatedByExistingSearchModal = () => {
    sendEvent('search_save_search_toggle_click', 'search', {
      event: {
        source: 'saved_search_similar_search_found_popup',
      },
    });
  };

  const onToggleChange = () => {
    onChange(true);
  };

  if (isButton) {
    return (
      <>
        {isToggled ? null : (
          <SaveSearchMobileButton
            onClick={onToggleChange}
            disabled={disabled}
          />
        )}
        {activeModal === SavedSearchActiveModal.PostSaved ? (
          <PostSavedModal
            onClose={handleClosePostSavedModal}
          />
        ) : null}
        {activeModal === SavedSearchActiveModal.ExistingSearch ? (
          <ExistingSearchModal
            onClose={() => setActiveModal(null)}
            onCreated={handleCreatedByExistingSearchModal}
            onUpdated={handleUpdatedByExistingSearchModal}
          />
        ) : null}
        {activeModal === SavedSearchActiveModal.EnhanceSearch ? (
          <EnhanceSearchModal
            onClose={() => setActiveModal(null)}
          />
        ) : null}
      </>
    );
  }

  return (
    <SaveSearchButtonWrapper data-auto="save-search-button-wrapper" data-auto-active={isToggled} noMargin={noMargin}>
      <ToggleField
        label={label || t('savedSearch.saveButton.labelDefault')}
        disabled={disabled}
        checked={isToggled}
        onChange={onToggleChange}
        labelTextMode={labelTextMode}
      />
      {activeModal === SavedSearchActiveModal.PostSaved ? (
        <PostSavedModal
          onClose={handleClosePostSavedModal}
          isDesktop
        />
      ) : null}
      {activeModal === SavedSearchActiveModal.ExistingSearch ? (
        <ExistingSearchModal
          isDesktop
          onClose={() => setActiveModal(null)}
          onCreated={handleCreatedByExistingSearchModal}
          onUpdated={handleUpdatedByExistingSearchModal}
        />
      ) : null}
      {activeModal === SavedSearchActiveModal.EnhanceSearch ? (
        <EnhanceSearchModal
          isDesktop
          onClose={() => setActiveModal(null)}
        />
      ) : null}
    </SaveSearchButtonWrapper>
  );
};

const mapStateToProps = (state: State) => ({
  saveData: addSaveSearchData(state),
  previousSavedSearchId: previousSavedSearchIdSelector(state),
  disabled: isSearchListLoadingSelector(state),
  isToggled: hasSavedSearch(state),
  maybeSaved: findSavedSearch(state),
  activeModal: savedSearchActiveModalSelector(state),
});

const mapDispatchToProps = {
  onSaveSearch: actions.saveSearchWithValidation,
  onDeleteSaveSearch: actions.deleteSaveSearch,
  setNotificationBannerMessage: actions.setNotificationBannerMessage,
  setActiveModal: actions.setSavedSearchActiveModal,
};

type SaveSearchButtonOwnProps = Pick<
  SaveSearchButtonProps,
  | 'isButton'
  | 'label'
  | 'labelTextMode'
  | 'noMargin'
>;

export default connect(
  mapStateToProps,
  mapDispatchToProps,
  ({ maybeSaved, saveData, ...stateProps }, dispatchProps, ownProps: SaveSearchButtonOwnProps): SaveSearchButtonProps => ({
    onChange: (saveSilently?: boolean) => {
      if (stateProps.isToggled) {
        dispatchProps.onDeleteSaveSearch(maybeSaved.searchId, { source: 'search_result_page' });
      }
      else {
        dispatchProps.onSaveSearch({
          ...saveData,
          updateFrequency: UpdateFrequency.Daily,
        }, { source: 'market_place', saveSilently });
      }
    },
    setActiveModal: dispatchProps.setActiveModal,
    setNotificationBannerMessage: dispatchProps.setNotificationBannerMessage,
    ...stateProps,
    ...ownProps,
  })
)(SaveSearchButton);
